import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetCombinationMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ combo_name: '', is_active: true });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadRows = async () => {
    const res = await fetch('/api/v1/banquet/combinations', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load combinations');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadRows().catch((err) => setError(err?.message || 'Failed to load combinations'));
  }, [chainId, propertyId, outletId]);

  const save = async () => {
    setError('');
    if (!form.combo_name) return setError('Combination name is required');
    const url = editingId ? `/api/v1/banquet/combinations/${editingId}` : '/api/v1/banquet/combinations';
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ combo_name: form.combo_name, is_active: form.is_active !== false })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    setEditingId('');
    setForm({ combo_name: '', is_active: true });
    loadRows();
  };

  const edit = (r) => {
    setEditingId(String(r.combo_id));
    setForm({ combo_name: r.combo_name || '', is_active: r.is_active !== false });
    scrollToForm();
  };

  const deactivate = async (comboId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/combinations/${comboId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadRows();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Combination Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <input placeholder="Combination name (ex: A+B)" value={form.combo_name} onChange={(e) => setForm({ ...form, combo_name: e.target.value })} />
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Combination' : 'Add Combination'}</button>
        {editingId ? <button onClick={() => { setEditingId(''); setForm({ combo_name: '', is_active: true }); }}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid var(--theme-border)' }}>
          <thead>
            <tr style={{ background: '#e6f0fa' }}>
              <th style={{ textAlign: 'left' }}>Combination</th>
              <th style={{ textAlign: 'right' }}>Total Capacity</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.combo_id}>
                <td>{r.combo_name}</td>
                <td style={{ textAlign: 'right' }}>{r.total_capacity}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.combo_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={4}>No combinations found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetCombinationMaster;
